import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/analytics";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatus("timeout");
        return;
      }
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
            trackEvent("purchase", {
              transaction_id: sessionId,
              value: (data.amount_total || 0) / 100,
              currency: (data.currency || "eur").toUpperCase(),
              items: [{ item_id: data.kind || "order", item_name: data.kind || "order" }],
            });
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-result">
      {status === "checking" && (
        <>
          <Loader2 className="w-16 h-16 mx-auto text-orange-600 animate-spin" />
          <h1 className="text-2xl font-black tracking-tight mt-6">Se verifică plata...</h1>
          <p className="text-slate-500 mt-2">Te rugăm să aștepți câteva momente.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-green-600" />
          <h1 className="text-3xl font-black tracking-tight mt-6" data-testid="payment-success">Plată reușită!</h1>
          <p className="text-slate-500 mt-3">Mulțumim pentru comandă. Vei primi un email de confirmare în curând.</p>
          <div className="flex gap-4 justify-center mt-8">
            <Link to="/cont" className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 font-bold uppercase tracking-wide text-sm transition-colors">Vezi comenzile</Link>
            <Link to="/produse" className="border border-slate-300 hover:border-slate-900 px-8 py-4 font-bold uppercase tracking-wide text-sm transition-colors">Continuă cumpărăturile</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-red-600" />
          <h1 className="text-2xl font-black tracking-tight mt-6">{status === "timeout" ? "Verificare întârziată" : "Plată nefinalizată"}</h1>
          <p className="text-slate-500 mt-3">
            {status === "timeout" ? "Statusul plății va fi actualizat în scurt timp. Verifică contul tău." : "Plata nu a putut fi confirmată. Încearcă din nou."}
          </p>
          <Link to="/cos" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 font-bold uppercase tracking-wide text-sm mt-8 transition-colors">
            Înapoi la coș <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      )}
    </div>
  );
}
